// crewbit-admin/src/hooks/useBusinessFinancialSettings.ts
//
// Hook contract: docs/references/MONEY_MODULE_SPEC_v1.md §Settings.5/6.
// Read-only paired-with-write hook for the Money §Settings hub. Reads
// `business_financial_settings` (13 fields) joined with `businesses.tax_mode`
// + 4 Stripe Connect flags. Writes flow through Session 137's
// `useUpdateBusinessMoneySettings` mutation hook.
//
// Mirrors crewbit-mobile/hooks/useBusinessFinancialSettings.ts (Session 138)
// with web-runtime divergences:
//   - useFocusEffect → document.addEventListener('visibilitychange') with
//     30s throttle (admin idiom from useFinancialSummary).
//   - DeviceEventEmitter → window.addEventListener(MONEY_MUTATION_EVENT).
//
// §Settings Phase 2A (2026-05-18): envelope expanded from 8 to 13 BFS fields.
// The original fiscal_year_start type-mismatch concern is resolved — the live
// update_business_money_settings RPC validates p_fiscal_year_start as int 1..12
// (verified via pg_get_functiondef 2026-05-18); no ::date coercion exists.
// quick_method_remittance_rate_override re-enters the envelope: live RPC
// signature does accept p_quick_method_remittance_rate_override (Migration 274,
// §WorldClass.2). The prior Session-139 scope-down ack is stale.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { ACTIVE_BUSINESS_CHANGED_EVENT } from './useActiveBusiness';
import { MONEY_MUTATION_EVENT } from './useFinancialSummary';

const FOCUS_REFETCH_THROTTLE_MS = 30_000;

export type TaxMode = 'charge' | 'exempt' | 'quick_method';

export type StripeConnectStatus =
  | 'not_connected'
  | 'charges_disabled'
  | 'payouts_disabled'
  | 'active';

export interface BusinessFinancialSettings {
  hstNumber: string | null;
  hstRate: number | null;
  defaultPaymentTerms: number | null;
  defaultDepositPercentage: number | null;
  mileageRate1: number | null;
  mileageRate2: number | null;
  mileageThreshold: number | null;
  tipsEnabled: boolean;
  // §Settings Phase 2A additions:
  fiscalYearStart: number | null;
  vehicleBusinessUse: number | null;
  telecomBusinessUse: number | null;
  homeOfficePercentage: number | null;
  quickMethodRemittanceRateOverride: number | null;
  taxMode: TaxMode | null;
  stripeConnectStatus: StripeConnectStatus;
}

export interface UseBusinessFinancialSettingsReturn {
  data: BusinessFinancialSettings | null;
  isLoading: boolean;
  isRefreshing: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

interface BusinessRow {
  id: string;
  tax_mode: TaxMode | null;
  stripe_connect_account_id: string | null;
  stripe_connect_onboarding_complete: boolean | null;
  stripe_connect_charges_enabled: boolean | null;
  stripe_connect_payouts_enabled: boolean | null;
}

interface BfsRow {
  hst_number: string | null;
  hst_rate: number | string | null;
  default_payment_terms: number | null;
  default_deposit_percentage: number | string | null;
  mileage_rate_1: number | string | null;
  mileage_rate_2: number | string | null;
  mileage_threshold: number | string | null;
  tips_enabled: boolean | null;
  // §Settings Phase 2A additions:
  fiscal_year_start: number | null;
  vehicle_business_use: number | string | null;
  telecom_business_use: number | string | null;
  home_office_percentage: number | string | null;
  quick_method_remittance_rate_override: number | string | null;
}

function deriveStripeStatus(b: BusinessRow): StripeConnectStatus {
  if (!b.stripe_connect_account_id || !b.stripe_connect_onboarding_complete) return 'not_connected';
  if (!b.stripe_connect_charges_enabled) return 'charges_disabled';
  if (!b.stripe_connect_payouts_enabled) return 'payouts_disabled';
  return 'active';
}

function num(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number') return v;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

const BFS_SELECT =
  'hst_number, hst_rate, default_payment_terms, default_deposit_percentage, ' +
  'mileage_rate_1, mileage_rate_2, mileage_threshold, tips_enabled, ' +
  'fiscal_year_start, vehicle_business_use, telecom_business_use, ' +
  'home_office_percentage, quick_method_remittance_rate_override';

const BUSINESS_SELECT =
  'id, tax_mode, stripe_connect_account_id, stripe_connect_onboarding_complete, ' +
  'stripe_connect_charges_enabled, stripe_connect_payouts_enabled';

export function useBusinessFinancialSettings(
  businessId: string | null,
): UseBusinessFinancialSettingsReturn {
  const [data, setData] = useState<BusinessFinancialSettings | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const dataRef = useRef<BusinessFinancialSettings | null>(null);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const lastFocusFetchRef = useRef<number>(0);
  const inFlightRef = useRef<boolean>(false);
  const mountedRef = useRef<boolean>(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const doFetch = useCallback(
    async (manual: boolean) => {
      if (!businessId) return;
      if (inFlightRef.current) return;
      inFlightRef.current = true;

      const isInitial = dataRef.current === null;
      if (isInitial) setIsLoading(true);
      else if (manual) setIsRefreshing(true);

      try {
        const [bfsResult, businessResult] = await Promise.all([
          supabase
            .from('business_financial_settings')
            .select(BFS_SELECT)
            .eq('business_id', businessId)
            .maybeSingle<BfsRow>(),
          supabase
            .from('businesses')
            .select(BUSINESS_SELECT)
            .eq('id', businessId)
            .maybeSingle<BusinessRow>(),
        ]);

        if (!mountedRef.current) return;

        if (bfsResult.error || businessResult.error) {
          setError(
            new Error(
              bfsResult.error?.message ??
                businessResult.error?.message ??
                'Failed to load settings',
            ),
          );
          return;
        }

        const bfs = bfsResult.data;
        const biz = businessResult.data;

        setData({
          hstNumber: bfs?.hst_number ?? null,
          hstRate: num(bfs?.hst_rate ?? null),
          defaultPaymentTerms: bfs?.default_payment_terms ?? null,
          defaultDepositPercentage: num(bfs?.default_deposit_percentage ?? null),
          mileageRate1: num(bfs?.mileage_rate_1 ?? null),
          mileageRate2: num(bfs?.mileage_rate_2 ?? null),
          mileageThreshold: num(bfs?.mileage_threshold ?? null),
          tipsEnabled: bfs?.tips_enabled ?? false,
          // §Settings Phase 2A additions:
          fiscalYearStart: bfs?.fiscal_year_start ?? null,
          vehicleBusinessUse: num(bfs?.vehicle_business_use ?? null),
          telecomBusinessUse: num(bfs?.telecom_business_use ?? null),
          homeOfficePercentage: num(bfs?.home_office_percentage ?? null),
          quickMethodRemittanceRateOverride: num(bfs?.quick_method_remittance_rate_override ?? null),
          taxMode: biz?.tax_mode ?? null,
          stripeConnectStatus: biz ? deriveStripeStatus(biz) : 'not_connected',
        });
        setError(null);
      } finally {
        if (mountedRef.current) {
          setIsLoading(false);
          setIsRefreshing(false);
        }
        inFlightRef.current = false;
      }
    },
    [businessId],
  );

  useEffect(() => {
    setData(null);
    setError(null);
  }, [businessId]);

  useEffect(() => {
    if (!businessId) return;
    void doFetch(false);
  }, [businessId, doFetch]);

  useEffect(() => {
    if (!businessId) return;
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastFocusFetchRef.current >= FOCUS_REFETCH_THROTTLE_MS) {
        lastFocusFetchRef.current = now;
        void doFetch(true);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [businessId, doFetch]);

  useEffect(() => {
    const onMutation = () => {
      if (businessId) void doFetch(true);
    };
    window.addEventListener(MONEY_MUTATION_EVENT, onMutation);
    window.addEventListener(ACTIVE_BUSINESS_CHANGED_EVENT, onMutation);
    return () => {
      window.removeEventListener(MONEY_MUTATION_EVENT, onMutation);
      window.removeEventListener(ACTIVE_BUSINESS_CHANGED_EVENT, onMutation);
    };
  }, [businessId, doFetch]);

  const refetch = useCallback(async () => {
    await doFetch(true);
  }, [doFetch]);

  return useMemo(
    () => ({ data, isLoading, isRefreshing, error, refetch }),
    [data, isLoading, isRefreshing, error, refetch],
  );
}
